import React, { useEffect, useState } from "react";
import Button from "components/crm/ui/Button";
import CheckRow from "components/crm/ui/CheckRow";
import Chip from "components/crm/ui/Chip";
import TextField from "components/crm/ui/TextField";
import { useToast } from "components/crm/ui/Toast";
import { useCrmLocale } from "components/crm/ui/crmTime";
import WhenPicker from "components/prospection/WhenPicker";
import { prospectionErrorText } from "helper/prospectionErrors";
import useText from "hooks/useText";
import { callMoments, defaultRetry, resolveWhen } from "pages/prospection/callTime";
import {
  MAX_ATTEMPTS,
  RESULTS,
  THIRD_ACTIONS,
  buildConfirmBody,
  canConfirm,
  confirmMessage,
  currentAttempt,
  ordinal,
} from "pages/prospection/qualification";
import { confirmCall } from "server/crm/prospectionApi";

const CLOCK_REFRESH_MS = 30000;

// Qualification of a call: result, note, follow-up, "Confirm".
// history: calls of the company (null while loading).
const CallQualification = ({ company, line, sessionId, history, onConfirmed }) => {
  const { text } = useText("prospection");
  const locale = useCrmLocale();
  const toast = useToast();
  const [now, setNow] = useState(() => new Date());
  const [result, setResult] = useState(null);
  const [note, setNote] = useState("");
  const [visit, setVisit] = useState(false);
  const [action, setAction] = useState("visit");
  const [callbackWhen, setCallbackWhen] = useState({
    choice: "tomorrow",
    day: null,
    time: "",
  });
  const [retryWhen, setRetryWhen] = useState(() => ({
    choice: defaultRetry(new Date()),
    day: null,
    time: "",
  }));
  const [busy, setBusy] = useState(false);

  // Keeps the moment chips in step with the clock.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const attempt = currentAttempt(history);
  const thirdAttempt = attempt >= MAX_ATTEMPTS;
  const moments = callMoments(now);
  let at = null;
  if (result === "callback") at = resolveWhen(callbackWhen, moments, now);
  if (result === "no_answer" && !thirdAttempt) {
    at = resolveWhen(retryWhen, moments, now);
  }
  const ready = canConfirm({ result, note, at, history, attempt });

  const confirm = async () => {
    setBusy(true);
    try {
      const body = buildConfirmBody({
        company,
        line,
        sessionId,
        result,
        note,
        visit,
        at,
        action,
        attempt,
      });
      const response = await confirmCall(body);
      onConfirmed({
        message: confirmMessage(response, {
          company,
          now: new Date(),
          text,
          locale,
        }),
        response,
      });
    } catch (err) {
      toast(
        text("qualification.saveError", {
          error: prospectionErrorText(text, err),
        }),
        "error"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="font-title text-sm font-medium leading-5 text-body">
        {text("qualification.title")}
      </p>
      <div className="flex flex-wrap gap-2">
        {RESULTS.map((id) => (
          <Chip key={id} selected={result === id} onClick={() => setResult(id)}>
            {text(`results.${id}`)}
          </Chip>
        ))}
      </div>

      {result === "answered" && (
        <CheckRow checked={visit} onClick={() => setVisit((value) => !value)}>
          {text("qualification.visit")}
        </CheckRow>
      )}

      {result === "callback" && (
        <WhenPicker
          id="callback-when"
          title={text("qualification.callbackTitle")}
          hint={text("qualification.callbackHint")}
          value={callbackWhen}
          onChange={setCallbackWhen}
          now={now}
        />
      )}

      {result === "no_answer" && history && !thirdAttempt && (
        <WhenPicker
          id="retry-when"
          title={text("qualification.retryTitle", {
            ordinal: ordinal(attempt, text),
          })}
          hint={text("qualification.retryHint", { n: attempt })}
          value={retryWhen}
          onChange={setRetryWhen}
          now={now}
        />
      )}

      {result === "no_answer" && history && thirdAttempt && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold leading-5 text-ink">
            {text("qualification.thirdTitle", {
              ordinal: ordinal(attempt, text),
            })}
          </p>
          <div role="radiogroup" className="flex flex-col gap-2">
            {THIRD_ACTIONS.map((id) => (
              <CheckRow
                key={id}
                checked={action === id}
                onClick={() => setAction(id)}
              >
                {text(`qualification.actions.${id}`)}
              </CheckRow>
            ))}
          </div>
        </div>
      )}

      <TextField
        id="call-note"
        multiline
        label={text("qualification.noteLabel")}
        hint={text(
          result === "no_answer"
            ? "qualification.noteOptional"
            : "qualification.noteRequired"
        )}
        placeholder={text("qualification.notePlaceholder")}
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />

      <div className="flex justify-end">
        <Button onClick={confirm} disabled={!ready || busy}>
          {busy ? text("qualification.saving") : text("qualification.confirm")}
        </Button>
      </div>
    </div>
  );
};

export default CallQualification;
